import { type FC, type MouseEvent, type ReactNode } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { usePrerenderedCheckout } from 'dibs-checkout/exports/prerender/usePrerenderedCheckout.ts';
import { useAddItemToCart } from 'dibs-buyer-layout/exports/useAddItemToCart.ts';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button, type ButtonProps } from 'dibs-elements/exports/Button.tsx';

// mutations

// helpers
import { getForcedCurrency } from 'dibs-currency-helpers/exports/getForcedCurrency.ts';
import { redirectToOMT } from './utils.ts';
import { type Maybe } from 'dibs-ts-utils/exports/Maybe.ts';
import { useAppDispatch } from '../hooks/useReactReduxHooks.ts';
import { trackPurchaseClick, locations, type Location } from '../tracking/actionButtonsTracking.ts';
import { setCartItems } from '../../actions/sharedMobileActions.js';
import { hidePage } from 'dibs-buyer-layout/exports/hidePage.ts';

import { type PdpSharedPurchaseButton_item$key } from './__generated__/PdpSharedPurchaseButton_item.graphql.ts';
import { type PdpSharedPurchaseButton_viewer$key } from './__generated__/PdpSharedPurchaseButton_viewer.graphql.ts';
import { useShowPdpSharedShoppingButtons } from '../shared/PdpSharedShoppingButtons/useShowPdpSharedShoppingButtons.ts';

// styles
import styles from './PdpSharedButtons.scss';

const itemFragment = graphql`
    fragment PdpSharedPurchaseButton_item on Item {
        isPrivateVisibilityType
        lastActiveTransaction(buyerId: $userId) {
            serviceId
            hasCompletedCheckout
        }
        hasPrivateListing
        ...usePrerenderedCheckout_item
        ...useAddItemToCart_item
        # used in trackPurchaseClick
        # eslint-disable-next-line relay/unused-fields
        pdpTrackingVpvParams(
            userId: $userId
            userCountryCode: $userCountryCode
            userZipCode: $userZipCode
        ) @include(if: $afterDisplay)
        ...actionButtonsTracking_item
    }
`;

const viewerFragment = graphql`
    fragment PdpSharedPurchaseButton_viewer on Viewer {
        ...useShowPdpSharedShoppingButtons_viewer
    }
`;

type Props = {
    item: PdpSharedPurchaseButton_item$key | null | undefined;
    viewer?: PdpSharedPurchaseButton_viewer$key | null;
    quantitySelected?: string;
    itemId: string;
    userId?: Maybe<string>;
    skuId?: string;
    label?: ReactNode;
    size?: 'small' | 'medium' | 'large';
    isPrimary?: boolean;
    disabled?: boolean;
    hasPrivateOffer?: Maybe<boolean>;
    location?: Location;
    className?: string;
};

const purchaseCta = (
    <FormattedMessage id="PdpSharedPurchaseButton.purchaseCta" defaultMessage="Purchase" />
);

const buyNowCta = (
    <FormattedMessage id="PdpSharedPurchaseButton.buyNowCta" defaultMessage="Buy Now" />
);

const acceptOfferCta = (
    <FormattedMessage id="PdpSharedPurchaseButton.acceptOfferCta" defaultMessage="Accept Offer" />
);

export const PdpSharedPurchaseButton: FC<Props> = ({
    item: itemRef,
    viewer: viewerRef,
    quantitySelected,
    itemId,
    userId,
    skuId,
    label,
    size = 'large',
    isPrimary = true,
    disabled = false,
    hasPrivateOffer = false,
    location,
    className = styles.button,
}) => {
    const environment = useRelayEnvironment();
    const dispatch = useAppDispatch();
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const showPdpSharedShoppingButtons = useShowPdpSharedShoppingButtons(viewer);

    const { redirect: redirectToCheckout } = usePrerenderedCheckout({
        hideParentPage: hidePage,
        item,
        params: {
            skuId,
            quantity: quantitySelected,
            offerCurrency: getForcedCurrency(),
            acceptOffer: hasPrivateOffer ? 'true' : undefined,
            triggerType: location === 'STICKY_HEADER' ? 'pdp-header-purchase' : undefined,
        },
    });
    const { addItemToCart } = useAddItemToCart({
        quantitySelected: quantitySelected || '1',
        item: item || null,
        userId: userId || null,
        skuId: skuId || null,
    });

    const addToCart = (): void => {
        if (showPdpSharedShoppingButtons) {
            return;
        }
        if (!userId) {
            dispatch(setCartItems(itemId));
        } else {
            addItemToCart({
                type: 'SAVE_FOR_LATER',
            });
        }
    };

    const onClick = (event?: MouseEvent): void => {
        const { serviceId: transactionId, hasCompletedCheckout } =
            item?.lastActiveTransaction || {};

        const hasPrivateListing = item?.hasPrivateListing;
        trackPurchaseClick({
            item,
            event,
            location,
            hasPrivateOffer,
            environment,
            quantity: Number(quantitySelected),
        });

        if (!hasPrivateListing) {
            addToCart();
        }

        if (transactionId && hasCompletedCheckout) {
            redirectToOMT(transactionId);
        } else {
            redirectToCheckout();
        }
    };

    const isPrivateVisibilityType = item?.isPrivateVisibilityType;
    const purchaseButton =
        !isPrivateVisibilityType && location !== locations.SIMILAR_SOLD ? buyNowCta : purchaseCta;
    const ctaCopy = hasPrivateOffer ? acceptOfferCta : purchaseButton;

    const additionalProps: Partial<ButtonProps> = {};
    if (isPrivateVisibilityType) {
        additionalProps.type = 'primaryAlt';
    } else if (!isPrimary) {
        additionalProps.type = 'secondary';
    }

    return (
        <Button
            className={className}
            dataTn="item-actions-purchase-item"
            onClick={onClick}
            size={size}
            disabled={disabled}
            fullWidth
            {...additionalProps}
        >
            {label ? <>{label}</> : ctaCopy}
        </Button>
    );
};
